import { z } from 'zod';
import { ReactElement, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import ModelForm from '@casops/components/layout/model-form/ModelForm';
import { ServerErrorFields } from '@dmc/ui';
import {
  ApprovalAction,
  AssetApprovalActionModel,
  AssetApprovalStatus,
} from '@casops/store/apis/assetApprovalsApi';
import useApprovalForm from './useApprovalForm';
import CasFormMultiselect from '@cas/components/inputs/cas-form-multiselect/CasFormMultiselect';
import useAssetGroupOptions from '@casops/side-panels/manage-geofence/components/general/useAssetGroupOptions';
import { useConfigGroupOptions } from './useConfigGroupOptions';
import CasFormDropdown from '@cas/components/inputs/cas-form-dropdown/CasFormDropdown';
import CasFormConditionalDisplay from '@cas/components/inputs/cas-form-conditional-display/CasFormConditionalDisplay';
import { AssetDeviceType } from '@casops/store/apis/registeredAssetsApi';
import { CanSubmitProps } from '@casops/components/layout/model-form/useModelForm';
import { ValidNameRegex } from '@casops/store/apis/commonModels';
import { useDeviceTypeOptions } from '@casops/side-panels/filter-panel/filters/device-types/DeviceTypesFilter';
import styles from '@casops/side-panels/manage-asset/components/asset-form/AssetForm.module.scss';
import { useWatch } from 'react-hook-form';

const DESCRIPTION_MAX = 511;
const ASSET_NAME_MAX = 8;

type FormModel = AssetApprovalActionModel;

const serverErrorFields: ServerErrorFields<FormModel> = {
  ags: 'AssetGroupIds',
  cfgId: 'ConfigurationGroupId',
  an: 'AssetName',
  desc: 'Description',
};

// Allow the form to be submitted without any changes so long as the fields are valid.
const canSubmitConflict = ({ isSubmitting, isValid }: CanSubmitProps): boolean => {
  return !isSubmitting && isValid;
};

interface AssetApprovalFormProps {
  id: number;
  approvalStatus: AssetApprovalStatus;
  action: FormModel;
  helperMessage?: ReactNode;
}

export const AssetApprovalForm = ({
  id,
  approvalStatus,
  action,
  helperMessage,
}: AssetApprovalFormProps): ReactElement => {
  const { t } = useTranslation();

  const schema = useMemo(() => {
    return z
      .object({
        an: z
          .string()
          .trim()
          .min(1, { message: t('Validation.SpecifyAssetName') })
          .max(ASSET_NAME_MAX, {
            message: t('Validation.AssetNameTooLong', { max: ASSET_NAME_MAX }),
          })
          .regex(ValidNameRegex, { message: t('Validation.InvalidCharacters') }),
        cfgId: z.number().nullable(), // Allow null (for PPU).
        ags: z
          .number()
          .array()
          .min(1, { message: t('Validation.SpecifyAssetGroups') }),
        desc: z
          .string()
          .max(DESCRIPTION_MAX, {
            message: t('Validation.DescriptionTooLong', { max: DESCRIPTION_MAX }),
          })
          .nullable(),
        // The following fields are not displayed, but they are needed for validation refinement.
        hw: z.nativeEnum(AssetDeviceType).nullable(),
        action: z.nativeEnum(ApprovalAction),
      })
      .superRefine(({ hw, cfgId, an, action: actionType }, ctx) => {
        // Require a config group for non-PPU devices.
        if (hw !== AssetDeviceType.PPU && cfgId === null) {
          ctx.addIssue({
            code: 'custom',
            message: t('Validation.SpecifyConfigGroup'),
            path: ['cfgId'],
          });
        }

        // Compare the asset name field against the original value.
        if (
          approvalStatus === AssetApprovalStatus.Conflict &&
          actionType === ApprovalAction.RegisterNewAsset &&
          an === action.an
        ) {
          ctx.addIssue({
            code: 'custom',
            message: t('Validation.UniqueName'),
            path: ['an'],
          });
        }
      });
  }, [t, approvalStatus, action.an]);

  const { canSubmit, onSubmit, errorMessage, control, resetField } = useApprovalForm({
    id,
    action,
    schema,
    serverErrorFields,
    // Change the submit button rules for conflict resolution.
    canSubmitCallback:
      approvalStatus === AssetApprovalStatus.Conflict ? canSubmitConflict : undefined,
  });

  const configGroupOptions = useConfigGroupOptions();
  const deviceTypeOptions = useDeviceTypeOptions();

  const assetName = useWatch({ control, name: 'an' });

  // Determine which fields should be visible.
  const showDeviceId = approvalStatus !== AssetApprovalStatus.Conflict && Boolean(action.did);
  const showDeviceName = approvalStatus !== AssetApprovalStatus.Conflict && Boolean(action.dn);
  const showDeviceType = approvalStatus !== AssetApprovalStatus.Conflict;
  const showMainFields =
    action.action === ApprovalAction.UpdatePreApproval ||
    action.action === ApprovalAction.RegisterNewAsset;
  const showConfigGroup =
    action.action !== ApprovalAction.UpdateDeviceName && action.hw !== AssetDeviceType.PPU;

  const submitLabel =
    action.action === ApprovalAction.UpdatePreApproval ? t('SaveChanges') : t('ApproveAsset');

  return (
    <ModelForm
      canSubmit={canSubmit}
      onSubmit={onSubmit}
      errorMessage={errorMessage}
      control={control}
      submitLabel={submitLabel}
    >
      <>
        <CasFormConditionalDisplay show={showDeviceType} name="hw" resetField={resetField}>
          <CasFormDropdown
            control={control}
            name="hw"
            label={t('Fields.DeviceType')}
            items={deviceTypeOptions}
            disabled={true}
          />
        </CasFormConditionalDisplay>

        <CasFormConditionalDisplay show={showDeviceId} name="did" resetField={resetField}>
          <CasFormInput control={control} name="did" label={t('Fields.DeviceId')} disabled={true} />
        </CasFormConditionalDisplay>

        <CasFormConditionalDisplay show={showDeviceName} name="dn" resetField={resetField}>
          <CasFormInput
            control={control}
            name="dn"
            label={t('Fields.DeviceName')}
            disabled={true}
          />
        </CasFormConditionalDisplay>

        <CasFormConditionalDisplay show={showMainFields} name="an" resetField={resetField}>
          <CasFormInput control={control} name="an" label={t('Fields.AssetName')} />
          {assetName && assetName.length >= 7 && (
            <div className={styles.warningMessage}>{t('Validation.TofLimitReached')}</div>
          )}
        </CasFormConditionalDisplay>

        <CasFormConditionalDisplay show={showConfigGroup} name="cfgId" resetField={resetField}>
          <CasFormDropdown
            control={control}
            name="cfgId"
            label={t('Fields.ConfigGroup')}
            items={configGroupOptions}
          />
        </CasFormConditionalDisplay>

        <CasFormConditionalDisplay show={showMainFields} name="ags" resetField={resetField}>
          <CasFormMultiselect<FormModel, number>
            control={control}
            name="ags"
            label={t('Fields.AssetGroup')}
            useItemsHook={useAssetGroupOptions}
            enableSelectAll={true}
          />
        </CasFormConditionalDisplay>

        <CasFormConditionalDisplay show={showMainFields} name="desc" resetField={resetField}>
          <CasFormInput
            control={control}
            name="desc"
            label={t('Fields.Description')}
            element="textarea"
            minHeight="100px"
          />
        </CasFormConditionalDisplay>

        {helperMessage}
      </>
    </ModelForm>
  );
};

export default AssetApprovalForm;
